import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { IMG } from "../data/worlds";

const lineVariant = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskedLine = ({ children, i, className }) => (
  <span className={`block overflow-hidden ${className || ""}`}>
    <motion.span className="block" custom={i} variants={lineVariant} initial="hidden" animate="show">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBlob1 = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -90]);
  const yBlob2 = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 70]);
  const yCover = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 60]);
  const rCover = useTransform(scrollYProgress, [0, 1], [2, reduce ? 2 : 5]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16" data-testid="hero-section">
      <motion.div className="blob floaty-slow" style={{ y: yBlob1, background: "rgba(217,83,56,0.16)", width: 320, height: 300, top: "8%", left: "-90px" }} />
      <motion.div className="blob floaty" style={{ y: yBlob2, background: "rgba(58,123,213,0.13)", width: 260, height: 250, bottom: "6%", left: "32%" }} />
      <motion.div className="blob floaty-slow" style={{ y: yBlob2, background: "rgba(229,169,60,0.18)", width: 220, height: 210, top: "12%", right: "-40px" }} />

      <div className="w-full relative z-10">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 w-full grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-10 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="eyebrow-label mb-6"
            data-testid="hero-brand"
          >
            VisualMA
          </motion.p>

          <h1 className="font-display font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6" data-testid="hero-headline">
            <MaskedLine i={0}>Lernen darf</MaskedLine>
            <MaskedLine i={1}>
              <em style={{ color: "var(--terra)" }}>schön</em> sein.
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="font-display text-lg sm:text-xl text-[color:var(--ink)] mb-4"
          >
            Lernwelten für kleine Entdecker ab 2 Jahren.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="text-[15px] sm:text-base text-[color:var(--ink-muted)] leading-relaxed max-w-md mb-9"
          >
            Liebevoll gestaltete Bücher und passende Lernlieder laden Kinder und Erwachsene zum
            gemeinsamen Sehen, Hören, Mitsingen und Entdecken ein.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="flex flex-col sm:flex-row gap-3.5"
          >
            <Link to="/lernwelten/farben" className="btn-primary" data-testid="hero-cta-primary">
              Erste Lernwelt entdecken
            </Link>
            <Link to="/ueber-visualma" className="btn-ghost" data-testid="hero-cta-secondary">
              VisualMA kennenlernen
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40, rotate: 5, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, rotate: 2, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          style={{ y: yCover, rotate: rCover }}
          className="relative w-full max-w-[580px] mx-auto"
        >
          <div className="blob" style={{ background: "rgba(74,139,93,0.13)", width: "112%", height: "114%", top: "-7%", left: "-6%" }} />
          <div className="relative rounded-[18px] sm:rounded-[24px] overflow-hidden" style={{ boxShadow: "0 30px 70px rgba(44,43,40,0.22)" }}>
            <img src={IMG.cover} alt="Kleine Welt, große Farben – das offizielle VisualMA Buchcover" className="w-full block" data-testid="hero-cover-image" />
          </div>
        </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.25 }}
          className="max-w-6xl mx-auto px-5 lg:px-8 mt-14 sm:mt-16"
          data-testid="hero-benefit-row"
        >
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3.5 border-t border-[color:var(--line)] pt-7">
            {[
              { t: "Ab 2 Jahren", c: "#4A8B5D" },
              { t: "Digitales Lernpaket", c: "#3A7BD5" },
              { t: "Mit eigenem Lernsong", c: "#E5A93C" },
              { t: "Sofort verfügbar", c: "#D95338" },
            ].map((b) => (
              <span key={b.t} className="inline-flex items-center gap-2.5 text-[11px] sm:text-[12px] font-semibold uppercase tracking-[0.16em] text-[color:var(--ink-muted)]">
                <span className="w-2 h-2 shrink-0" style={{ background: b.c, borderRadius: "58% 42% 55% 45% / 48% 55% 45% 52%" }} />
                {b.t}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
